import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../styles/formularios.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Swal from 'sweetalert2';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { resolveApiBaseList } from '../utils/apiBase';

const parseIsoDateToLocal = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);
    return new Date(year, monthIndex, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const obtenerIdUsuarioSesion = () =>
  sessionStorage.getItem('idUsuario') || localStorage.getItem('idUsuario');

const ConsultarInfoDocente = () => {
  // Estados para los datos del docente
  const [docenteData, setDocenteData] = useState({
    IdDocente: '',
    Nombre: '',
    ApellidoPaterno: '',
    ApellidoMaterno: '',
    Telefono: '',
    Puesto: '',
    Correo: '',
    Sexo: '',
    TipoSangre: '',
    fechaNacimiento: ''
  });

  const [loading, setLoading] = useState(true);
  const [isModified, setIsModified] = useState(false);
  
  // Estados para cambio de contraseña
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    contrasenaActual: '',
    nuevaContrasena: '',
    confirmarContrasena: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    actual: false,
    nueva: false,
    confirmar: false
  });

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;

    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;

      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch {
            // mantener mensaje original
          }

          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (!trimmed) {
          return { data: {}, baseUrl: normalizedBase };
        }

        try {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase };
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: no se pudo parsear JSON desde ${url}`, parseErr);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  // Función para obtener la información del docente logueado
  const cargarInfoDocente = useCallback(async () => {
    try {
      setLoading(true);
      
  const idUsuario = obtenerIdUsuarioSesion();
  console.log('🔍 IdUsuario disponible (session/local storage):', idUsuario);
  console.log('� sessionStorage:', sessionStorage);
  console.log('📦 localStorage:', localStorage);
      
      if (!idUsuario) {
        console.error('❌ No se encontró idUsuario en sessionStorage');
        Swal.fire({
          title: 'Error',
          text: 'No se encontró información de sesión. Por favor, inicia sesión nuevamente.',
          icon: 'error'
        });
        setLoading(false);
        return;
      }

      console.log('🔍 Cargando información del docente con idUsuario:', idUsuario);

      // Consultar información del docente
      const { data } = await fetchJsonWithFallback(`/calificaciones/docente-info?idUsuario=${idUsuario}`);
      console.log('📦 Información del docente cargada:', data);

      if (data.success && data.docente) {
        setDocenteData({
          idDocente: data.docente.IdDocente || '',
          nombre: data.docente.Nombre || '',
          apellidoPaterno: data.docente.ApellidoPaterno || '',
          apellidoMaterno: data.docente.ApellidoMaterno || '',
          telefono: data.docente.Telefono || '',
          puesto: data.docente.Puesto || '',
          correo: data.docente.Correo || '',
          sexo: data.docente.Sexo || '',
          tipoSangre: data.docente.TipoSangre || '',
          curp: data.docente.CURP || '',
          fechaNacimiento: (() => {
            const fecha = parseIsoDateToLocal(data.docente.FechaNacimiento);
            return fecha ? fecha.toLocaleDateString('es-MX') : '';
          })()
        });
      } else {
        throw new Error(data.message || 'No se encontró información del docente');
      }

    } catch (error) {
      console.error('💥 Error al cargar información del docente:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error al cargar la información: ' + error.message,
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    cargarInfoDocente();
  }, [cargarInfoDocente]);

  // Función para manejar cambios en el teléfono (solo números y máximo 10 dígitos)
  const handleTelefonoChange = (e) => {
    const value = e.target.value;
    // Solo permitir números y máximo 10 dígitos
    const numericValue = value.replace(/\D/g, '').slice(0, 10);
    
    setDocenteData(prev => ({
      ...prev,
      telefono: numericValue
    }));
    setIsModified(true);
  };

  // Función para manejar cambios en los otros inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDocenteData(prev => ({
      ...prev,
      [name]: value
    }));
    setIsModified(true);
  };

  // Función para modificar la información del docente
  const modificarInfoDocente = async () => {
    try {
      if (!isModified) {
        Swal.fire({
          title: 'Sin cambios',
          text: 'No se han realizado modificaciones',
          icon: 'info'
        });
        return;
      }

      console.log('🔄 Modificando información del docente:', docenteData);
      console.log('📋 Datos que se enviarán:');
      console.log('  - nombre:', docenteData.nombre);
      console.log('  - apellidoPaterno:', docenteData.apellidoPaterno);
      console.log('  - apellidoMaterno:', docenteData.apellidoMaterno);
      console.log('  - correo:', docenteData.correo);
      console.log('  - telefono (original):', docenteData.telefono, '(tipo:', typeof docenteData.telefono, ')');
      console.log('  - telefono (parseInt):', parseInt(docenteData.telefono), '(tipo:', typeof parseInt(docenteData.telefono), ')');

      // Confirmar modificación
      const result = await Swal.fire({
        title: '¿Confirmar modificación?',
        text: '¿Estás seguro de que deseas guardar los cambios?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, modificar',
        cancelButtonText: 'Cancelar'
      });

      if (!result.isConfirmed) return;

      // Enviar datos al servidor
      const { data: respuestaModificacion } = await fetchJsonWithFallback('/docentes/modificar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nombre: docenteData.nombre,
          apellidoPaterno: docenteData.apellidoPaterno,
          apellidoMaterno: docenteData.apellidoMaterno,
          correo: docenteData.correo,
          telefono: parseInt(docenteData.telefono)
        })
      });

      console.log('✅ Resultado exitoso:', respuestaModificacion);

      if (respuestaModificacion?.success === false) {
        throw new Error(respuestaModificacion.message || 'Error al modificar la información');
      }

      Swal.fire({
        title: '¡Éxito!',
        text: 'Información modificada correctamente',
        icon: 'success'
      });
      setIsModified(false);
      // Recargar la información para mostrar los cambios
      await cargarInfoDocente();

    } catch (error) {
      console.error('💥 Error al modificar información:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error al guardar los cambios: ' + error.message,
        icon: 'error'
      });
    }
  };

  // Funciones para cambio de contraseña
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Función de validación removida - no se requiere validación de formato

  const cambiarContrasena = async () => {
    try {
      // Validaciones
      if (!passwordData.contrasenaActual || !passwordData.nuevaContrasena || !passwordData.confirmarContrasena) {
        Swal.fire({
          title: 'Campos incompletos',
          text: 'Todos los campos son obligatorios',
          icon: 'error'
        });
        return;
      }

      if (passwordData.nuevaContrasena !== passwordData.confirmarContrasena) {
        Swal.fire({
          title: 'Error',
          text: 'Las contraseñas nuevas no coinciden',
          icon: 'error'
        });
        return;
      }

      // Validación deshabilitada - cualquier contraseña es válida
      // if (!validarNuevaContrasena(passwordData.nuevaContrasena)) {
      //   Swal.fire({
      //     title: 'Contraseña no válida',
      //     text: 'La nueva contraseña debe tener mínimo 8 caracteres, incluir mayúsculas, minúsculas, números y símbolos especiales (@$!%*?&)',
      //     icon: 'error'
      //   });
      //   return;
      // }

  const idUsuario = obtenerIdUsuarioSesion();
      if (!idUsuario) {
        Swal.fire({
          title: 'Error',
          text: 'No se encontró información de sesión',
          icon: 'error'
        });
        return;
      }

      const { data: cambioPassword } = await fetchJsonWithFallback('/docentes/cambiar-contrasena', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idUsuario: parseInt(idUsuario),
          contrasenaActual: passwordData.contrasenaActual,
          nuevaContrasena: passwordData.nuevaContrasena
        })
      });

      if (cambioPassword?.success) {
        Swal.fire({
          title: '¡Éxito!',
          text: cambioPassword.message || 'Contraseña cambiada correctamente',
          icon: 'success'
        });
        
        // Resetear formulario y cerrar modal
        setPasswordData({
          contrasenaActual: '',
          nuevaContrasena: '',
          confirmarContrasena: ''
        });
        setShowPasswordModal(false);
      } else {
        throw new Error(cambioPassword?.message || 'Error al cambiar la contraseña');
      }

    } catch (error) {
      console.error('💥 Error al cambiar contraseña:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error de conexión al cambiar la contraseña',
        icon: 'error'
      });
    }
  };

  const abrirModalContrasena = () => {
    setPasswordData({
      contrasenaActual: '',
      nuevaContrasena: '',
      confirmarContrasena: ''
    });
    setShowPasswordModal(true);
  };

  // Mostrar loading mientras carga
  if (loading) {
    return (
      <div className="container py-4 px-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3">Cargando información del docente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />

        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Información del Docente</small>
        </div>

        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Formulario */}
      <div className="p-4 bg-light rounded shadow-sm">
        <form className="row g-3">
          {/* Campos no editables */}
          <div className="col-md-3">
            <label htmlFor="idDocente" className="form-label">ID Docente</label>
            <input 
              type="text" 
              className="form-control" 
              id="idDocente" 
              value={docenteData.idDocente || ''} 
              disabled 
            />
          </div>

          {/* Campos no editables */}
          <div className="col-md-3">
            <label htmlFor="puesto" className="form-label">Puesto</label>
            <input 
              type="text" 
              className="form-control" 
              id="puesto" 
              value={docenteData.puesto || ''} 
              disabled
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="nombre" className="form-label">Nombre(s)</label>
            <input 
              type="text" 
              className="form-control" 
              id="nombre" 
              value={docenteData.nombre || ''} 
              disabled
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="apellidoPaterno" className="form-label">Apellido Paterno</label>
            <input 
              type="text" 
              className="form-control" 
              id="apellidoPaterno" 
              value={docenteData.apellidoPaterno || ''} 
              disabled
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="apellidoMaterno" className="form-label">Apellido Materno</label>
            <input 
              type="text" 
              className="form-control" 
              id="apellidoMaterno" 
              value={docenteData.apellidoMaterno || ''} 
              disabled
            />
          </div>

          {/* Campos no editables */}
          <div className="col-md-4">
            <label htmlFor="curp" className="form-label">Contraseña</label>
            <input 
              type="text" 
              className="form-control" 
              id="curp" 
              value={docenteData.curp || ''} 
              disabled 
            />
          </div>

          <div className="col-md-2">
            <label htmlFor="sexo" className="form-label">Sexo</label>
            <input 
              type="text" 
              className="form-control" 
              id="sexo" 
              value={docenteData.sexo || ''} 
              disabled 
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="fechaNacimiento" className="form-label">Fecha de Nacimiento</label>
            <input
              type="text"
              className="form-control"
              id="fechaNacimiento"
              value={docenteData.fechaNacimiento || ''}
              disabled
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="correo" className="form-label">Correo</label>
            <input 
              type="email" 
              className="form-control" 
              id="correo" 
              name="correo"
              value={docenteData.correo || ''} 
              onChange={handleInputChange}
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="tipoSangre" className="form-label">Tipo de Sangre</label>
            <input 
              type="text" 
              className="form-control" 
              id="tipoSangre" 
              value={docenteData.tipoSangre || ''} 
              disabled
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="telefono" className="form-label">Teléfono</label>
            <input 
              type="text" 
              className="form-control" 
              id="telefono" 
              name="telefono"
              value={docenteData.telefono || ''} 
              onChange={handleTelefonoChange}
              placeholder="Máximo 10 dígitos"
              maxLength="10"
            />
            <div className="form-text">
              Solo números, máximo 10 dígitos
            </div>
          </div>
          
          <div className="col-md-12 text-end mt-3">
            <button 
              type="button"
              className="btn btn-info px-4 me-2"
              onClick={abrirModalContrasena}
            >
              Cambiar Contraseña
            </button>
            <button 
              type="button"
              className={`btn px-4 ${isModified ? 'btn-warning' : 'btn-secondary'}`}
              onClick={modificarInfoDocente}
              disabled={!isModified}
            >
              {isModified ? 'Modificar' : 'Sin cambios'}
            </button>            
          </div>
        </form>
      </div>

      {/* Modal para cambiar contraseña */}
      {showPasswordModal && (
        <div className="modal fade show" style={{ display: 'block' }} aria-labelledby="modalPasswordLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="modalPasswordLabel">Cambiar Contraseña</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowPasswordModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => e.preventDefault()}>
                  {/* Contraseña actual */}
                  <div className="mb-3">
                    <label htmlFor="contrasenaActual" className="form-label">
                      Contraseña Actual <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type={showPasswords.contrasenaActual ? "text" : "password"}
                        className="form-control"
                        id="contrasenaActual"
                        value={passwordData.contrasenaActual}
                        onChange={(e) => handlePasswordChange('contrasenaActual', e.target.value)}
                        required
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => togglePasswordVisibility('contrasenaActual')}
                      >
                        <i className={`fas ${showPasswords.contrasenaActual ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Nueva contraseña */}
                  <div className="mb-3">
                    <label htmlFor="nuevaContrasena" className="form-label">
                      Nueva Contraseña <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type={showPasswords.nuevaContrasena ? "text" : "password"}
                        className="form-control"
                        id="nuevaContrasena"
                        value={passwordData.nuevaContrasena}
                        onChange={(e) => handlePasswordChange('nuevaContrasena', e.target.value)}
                        required
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => togglePasswordVisibility('nuevaContrasena')}
                      >
                        <i className={`fas ${showPasswords.nuevaContrasena ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                    <div className="form-text">
                      Ingresa tu nueva contraseña
                    </div>
                  </div>

                  {/* Confirmar nueva contraseña */}
                  <div className="mb-3">
                    <label htmlFor="confirmarContrasena" className="form-label">
                      Confirmar Nueva Contraseña <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type={showPasswords.confirmarContrasena ? "text" : "password"}
                        className={`form-control ${
                          passwordData.confirmarContrasena && 
                          passwordData.nuevaContrasena !== passwordData.confirmarContrasena 
                            ? 'is-invalid' : ''
                        }`}
                        id="confirmarContrasena"
                        value={passwordData.confirmarContrasena}
                        onChange={(e) => handlePasswordChange('confirmarContrasena', e.target.value)}
                        required
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => togglePasswordVisibility('confirmarContrasena')}
                      >
                        <i className={`fas ${showPasswords.confirmarContrasena ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                    {passwordData.confirmarContrasena && 
                     passwordData.nuevaContrasena !== passwordData.confirmarContrasena && (
                      <div className="invalid-feedback">
                        Las contraseñas no coinciden
                      </div>
                    )}
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={cambiarContrasena}
                  disabled={
                    !passwordData.contrasenaActual || 
                    !passwordData.nuevaContrasena || 
                    !passwordData.confirmarContrasena ||
                    passwordData.nuevaContrasena !== passwordData.confirmarContrasena
                  }
                >
                  Cambiar Contraseña
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop del modal */}
      {showPasswordModal && (
        <div 
          className="modal-backdrop fade show"
          onClick={() => setShowPasswordModal(false)}
        ></div>
      )}
    </div>
  );
};

export default ConsultarInfoDocente;
